import React from "react";
import { motion } from "framer-motion";
import { Compass, Layers, Cpu, Target } from "lucide-react";
import { SECTION } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";

const ICONS = [Compass, Layers, Cpu, Target];
const ACCENTS = ["gold", "cyan", "gold", "cyan"];

export default function ValueProposition() {
  const { t } = useI18n();
  const v = t.value;

  return (
    <section data-testid={SECTION.valueProp} className="section-y bg-white">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="text-overline">{v.overline}</span>
            <h2 className="mt-4 font-display font-semibold text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
              {v.title}
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed">{v.paragraph}</p>
            <div className="mt-8 p-6 rounded-2xl border-l-4 border-gold bg-amber-50/40">
              <p className="font-display text-lg text-navy leading-relaxed">{v.quote}</p>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {v.dimensions.map((d, i) => {
              const Icon = ICONS[i] || Compass;
              const accent = ACCENTS[i] || "cyan";
              return (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group relative p-7 rounded-2xl border border-slate-200 bg-white hover:border-navy/30 hover:shadow-xl transition-all duration-300"
                  data-testid={`value-dimension-${i}`}
                >
                  <div
                    className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${
                      accent === "gold" ? "bg-amber-50 text-amber-600" : "bg-cyan-50 text-cyan-700"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-navy">{d.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.desc}</p>
                  <span className="absolute top-6 right-6 text-xs font-mono text-slate-300">
                    0{i + 1}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
